import { useCallback } from "react";
import { useNodeId } from "@xyflow/react";
import { useSetAtom } from "jotai";
import classNames from "classnames";
import { Icon, ServiceIcon } from "@zapier/design-system";
import { Button } from "@zapier/design-system-beta";
import {
  type Actor,
  sidebarOpenNodeIdAtom,
  selectSingleNodeAtom,
} from "@zapier/canvas-state";
import useServiceById from "../../hooks/useServiceById";
import cleanUpAppId from "../../utils/cleanUpAppId";
import getAppIconUrl from "../../utils/getAppIconUrl";
import { StepSummaryItem } from "../../utils/getItemsForStepSummary";
import styles from "./StepSummary.module.css";

const size = 20;

type AppRowProps = {
  appId: string;
};

function AppRow({ appId }: AppRowProps) {
  const getService = useServiceById();
  const service = getService(appId);
  const serviceName = service?.name ?? cleanUpAppId(appId);
  const images = service?.images;

  return (
    <div className={styles.row}>
      <div className={styles.label}>App</div>
      <div className={classNames(styles.content, styles.appRowContent)}>
        <ServiceIcon
          alt={`${serviceName} icon`}
          hasBorder={false}
          size={size}
          hasShadow={false}
          serviceName={serviceName}
          src={getAppIconUrl(appId, images, size)}
        />
        <div className={styles.truncateText}>{serviceName}</div>
      </div>
    </div>
  );
}

type AssigneeRowProps = {
  actor: Actor;
};

function AssigneeRow({ actor }: AssigneeRowProps) {
  return (
    <div className={styles.row}>
      <div className={styles.label}>Assigned</div>
      <div className={styles.content}>
        <Icon
          name="personCircleFill"
          color={actor.color || "GrayWarm3"}
          size={size} // To matches service icon
        />
        <div className={styles.truncateText}>{actor.label}</div>
      </div>
    </div>
  );
}

function NotesRow() {
  const id = useNodeId();
  const setSidebarOpenNodeId = useSetAtom(sidebarOpenNodeIdAtom);
  const selectSingleNode = useSetAtom(selectSingleNodeAtom);

  const openSidebar = useCallback(() => {
    if (id !== null) {
      setSidebarOpenNodeId(id);
      selectSingleNode(id);
    }
  }, [selectSingleNode, setSidebarOpenNodeId, id]);

  return (
    <div className={classNames(styles.row, styles.notesRow)}>
      <div className={styles.label}>Notes</div>
      <div className={styles.content}>
        <Button onPress={openSidebar} variant="ghost" size="compact">
          View note
        </Button>
      </div>
    </div>
  );
}

type Props = {
  items: StepSummaryItem[];
  isAssetNodeType: boolean;
};

export function StepSummary({ items, isAssetNodeType }: Props) {
  return (
    <div
      className={classNames(styles.container, {
        [styles.assetNodeContainer]: isAssetNodeType,
      })}
    >
      {items.map((item) => {
        if (item.type === "app") {
          return <AppRow key="app" appId={item.appId} />;
        }
        if (item.type === "actor") {
          return <AssigneeRow key="actor" actor={item.actor} />;
        }
        if (item.type === "notes") {
          return <NotesRow key="notes" />;
        }
        return null;
      })}
    </div>
  );
}
